// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Arrange pipeline nodes in topological order along a horizontal line.
 */

import type { Edge, Node } from '@xyflow/react'
import type { PipelineNodeData } from '../../../core/contracts'

export interface ArrangePipelineOptions {
  stepX: number
  startX?: number
  defaultY?: number
  filter?: (node: Node<PipelineNodeData>) => boolean
}

/**
 * Arrange nodes horizontally in topological order (respecting edge dependencies).
 * Nodes not matching the filter keep their original positions.
 */
export function arrangePipelineNodes(
  nodes: Node<PipelineNodeData>[],
  edges: Edge[],
  { stepX, startX = 0, defaultY = 0, filter }: ArrangePipelineOptions,
): Node<PipelineNodeData>[] {
  const targetNodes = filter ? nodes.filter(filter) : nodes
  if (targetNodes.length === 0) return nodes

  const ordered = topologicalOrder(targetNodes, edges)

  // Use existing min positions as baseline, or fall back to defaults
  const minX = Math.min(...ordered.map(n => n.position.x).filter(Number.isFinite))
  const minY = Math.min(...ordered.map(n => n.position.y).filter(Number.isFinite))
  const baseX = Number.isFinite(minX) ? minX : startX
  const baseY = Number.isFinite(minY) ? minY : defaultY

  const positions = new Map(ordered.map((node, i) => [node.id, { x: baseX + i * stepX, y: baseY }]))

  return nodes.map(node => {
    const newPos = positions.get(node.id)
    return newPos ? { ...node, position: newPos } : node
  })
}

/**
 * Kahn's algorithm for topological sort with stable tie-breaking.
 * When no edges exist, preserves original array order.
 */
function topologicalOrder(
  nodes: Node<PipelineNodeData>[],
  edges: Edge[],
): Node<PipelineNodeData>[] {
  if (edges.length === 0) return nodes

  const nodeMap = new Map(nodes.map(n => [n.id, n]))
  const children = new Map<string, string[]>(nodes.map(n => [n.id, []]))
  const indegree = new Map<string, number>(nodes.map(n => [n.id, 0]))

  // Build graph from edges within our node set
  for (const { source, target } of edges) {
    if (nodeMap.has(source) && nodeMap.has(target)) {
      children.get(source)?.push(target)
      indegree.set(target, (indegree.get(target) ?? 0) + 1)
    }
  }

  // Track original array index for stable tie-breaking
  const originalIndex = new Map(nodes.map((n, i) => [n.id, i]))
  const byArrayOrder = (a: Node<PipelineNodeData>, b: Node<PipelineNodeData>) =>
    (originalIndex.get(a.id) ?? 0) - (originalIndex.get(b.id) ?? 0)

  // Start with nodes that have no incoming edges, sorted by array order
  const queue = nodes
    .filter(n => (indegree.get(n.id) ?? 0) === 0)
    .sort(byArrayOrder)

  const result: Node<PipelineNodeData>[] = []
  const visited = new Set<string>()

  while (queue.length > 0) {
    const node = queue.shift()!
    if (visited.has(node.id)) continue

    result.push(node)
    visited.add(node.id)

    // Add children whose dependencies are satisfied
    for (const childId of children.get(node.id) ?? []) {
      const remaining = (indegree.get(childId) ?? 0) - 1
      indegree.set(childId, remaining)

      if (remaining <= 0 && !visited.has(childId)) {
        const child = nodeMap.get(childId)
        if (child) queue.push(child)
      }
    }

    queue.sort(byArrayOrder)
  }

  // Add any remaining nodes (handles cycles)
  for (const node of nodes) {
    if (!visited.has(node.id)) result.push(node)
  }

  return result
}
